import { storage } from '../../lib/firebase';
import { ref, uploadBytes, deleteObject, getDownloadURL } from 'firebase/storage';
import { convertToWebP, generateCleanFilename } from '../../utils/image';
import { isEmulator } from '../../utils/environment';

interface UseTeamMemberPhotoReturn {
  uploadPhoto: (
    file: File,
    orgId: string,
    teamMemberId: string,
    currentPhotoUrl?: string
  ) => Promise<string>;
}

export const useTeamMemberPhoto = (): UseTeamMemberPhotoReturn => {
  const uploadPhoto = async (
    file: File,
    orgId: string,
    teamMemberId: string,
    currentPhotoUrl?: string
  ): Promise<string> => {
    try {
      // Convert and compress image to WebP
      const webpBlob = await convertToWebP(file);

      // Generate clean filename with organization ID for folder structure
      // and include team member ID in the filename for uniqueness
      const cleanOriginalName = `${teamMemberId}_${file.name}`;
      const filename = generateCleanFilename(cleanOriginalName, orgId);
      const fullPath = `team-headshots/${filename}`;

      // Delete old photo if it exists
      if (currentPhotoUrl) {
        if (currentPhotoUrl.includes('team-headshots')) {
          try {
            // Extract the path from emulator URL format
            const match = currentPhotoUrl.match(/team-headshots%2F(.*?)(?:\?|$)/);

            if (match && match[1]) {
              // Always use production path
              const currentPath = `heyisa-media/team-headshots/${decodeURIComponent(match[1])}`;
              const currentRef = ref(storage, currentPath);
              await deleteObject(currentRef);
            }
          } catch (error) {
            console.warn('Could not delete old photo:', error);
            // Continue with upload even if delete fails
          }
        }
      }

      const storageRef = ref(storage, `heyisa-media/${fullPath}`);
      await uploadBytes(storageRef, webpBlob, {
        contentType: 'image/webp',
      });

      // Get the download URL
      const downloadUrl = await getDownloadURL(storageRef);
      return downloadUrl;
    } catch (error) {
      console.error('Error handling team member photo upload:', error);
      throw new Error('Failed to upload team member photo');
    }
  };

  return { uploadPhoto };
};
